/**
 * Ethos Virtual — Agent Onboarding Intake
 *
 * Shared types + metadata for the post-checkout wizard that configures and
 * provisions a client's Retell AI phone receptionist. The wizard collects this
 * config; the provisioning layer (src/lib/retell/provision.ts) maps it to
 * Retell `retell_llm_dynamic_variables` and a Cal.com booking link.
 *
 * Field → Retell mapping mirrors the demo template
 * (pathos-panel/ethos-sales-engine/src/voice/demoAgent.ts): business_name,
 * city, province, category, services, faq_summary, greeting,
 * books_appointments, after_hours.
 */

// ─── Option lists ────────────────────────────────────────────────────────────

/**
 * Languages the receptionist can be requested in. Availability is NOT
 * guaranteed — the wizard shows the approval caveat and provisioning honors
 * only approved languages.
 */
export const LANGUAGE_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "nl", label: "Dutch" },
  { code: "ru", label: "Russian" },
  { code: "uk", label: "Ukrainian" },
  { code: "pl", label: "Polish" },
  { code: "ro", label: "Romanian" },
  { code: "el", label: "Greek" },
  { code: "hu", label: "Hungarian" },
  { code: "cs", label: "Czech" },
  { code: "sk", label: "Slovak" },
  { code: "hr", label: "Croatian" },
  { code: "sr", label: "Serbian" },
  { code: "bg", label: "Bulgarian" },
  { code: "sv", label: "Swedish" },
  { code: "no", label: "Norwegian" },
  { code: "da", label: "Danish" },
  { code: "fi", label: "Finnish" },
  { code: "ca", label: "Catalan" },
  { code: "tr", label: "Turkish" },
  { code: "ar", label: "Arabic" },
  { code: "he", label: "Hebrew" },
  { code: "fa", label: "Persian" },
  { code: "ur", label: "Urdu" },
  { code: "hi", label: "Hindi" },
  { code: "pa", label: "Punjabi" },
  { code: "bn", label: "Bengali" },
  { code: "gu", label: "Gujarati" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "ml", label: "Malayalam" },
  { code: "ne", label: "Nepali" },
  { code: "zh", label: "Mandarin" },
  { code: "yue", label: "Cantonese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "vi", label: "Vietnamese" },
  { code: "th", label: "Thai" },
  { code: "id", label: "Indonesian" },
  { code: "ms", label: "Malay" },
  { code: "tl", label: "Tagalog" },
  { code: "so", label: "Somali" },
  { code: "sw", label: "Swahili" },
  { code: "am", label: "Amharic" },
];

/** Speaking-style keys understood by the blueprint prompt builder. */
export type SpeechStyle =
  | "friendly-warm"
  | "professional-neutral"
  | "energetic-upbeat"
  | "calm-reassuring";

export interface VoiceOption {
  /** Real Retell voice id, e.g. "retell-Marissa". Passed straight to the agent. */
  id: string;
  /** Display name shown in the picker. */
  label: string;
  /** One-line description (tone + accent). */
  description: string;
  /** Accent bucket, used for grouping in pickers (demo widget, wizard). */
  accent: string;
  /**
   * Self-hosted preview clip (public/voices/*.mp3) so the wizard can play a
   * real sample of this voice. Self-hosted (not the Retell S3 URL) to satisfy
   * the app's media-src CSP and to play instantly.
   */
  sampleUrl: string;
  /** Highlighted as the default / recommended voice. */
  recommended?: boolean;
  /** Which speaking-style prose the provisioning prompt should use for this voice. */
  speechStyle: SpeechStyle;
}

/**
 * Named voices mapped to real Retell voice ids (verified via list-voices).
 * Every clip in public/voices/ has been transcription-checked: no vendor/brand
 * mentions, and non-English clips are intentional for accented voices so the
 * sample demonstrates the accent (Spanish, Mandarin, French, German, Japanese).
 * Marissa must stay first: emptyConfig() defaults to VOICE_OPTIONS[0].
 */
export const VOICE_OPTIONS: ReadonlyArray<VoiceOption> = [
  { id: "retell-Marissa", label: "Marissa", description: "Warm & clear · American", accent: "American", sampleUrl: "/voices/marissa.mp3", recommended: true, speechStyle: "friendly-warm" },
  { id: "openai-Nova", label: "Nova", description: "Energetic & upbeat · American", accent: "American", sampleUrl: "/voices/nova.mp3", speechStyle: "energetic-upbeat" },
  { id: "11labs-Billy", label: "Billy", description: "Easygoing · American", accent: "American", sampleUrl: "/voices/billy.mp3", speechStyle: "energetic-upbeat" },
  { id: "retell-Willa", label: "Willa", description: "Bright & friendly · British", accent: "British", sampleUrl: "/voices/willa.mp3", speechStyle: "friendly-warm" },
  { id: "11labs-Anthony", label: "Anthony", description: "Composed & professional · British", accent: "British", sampleUrl: "/voices/anthony.mp3", speechStyle: "professional-neutral" },
  { id: "11labs-Monika", label: "Monika", description: "Poised & professional · Indian", accent: "Indian", sampleUrl: "/voices/monika.mp3", speechStyle: "professional-neutral" },
  { id: "11labs-Samad", label: "Samad", description: "Courteous & helpful · Indian", accent: "Indian", sampleUrl: "/voices/samad.mp3", speechStyle: "friendly-warm" },
  { id: "11labs-Amritanshu", label: "Amritanshu", description: "Refined & gracious · Indian", accent: "Indian", sampleUrl: "/voices/amritanshu.mp3", speechStyle: "professional-neutral" },
  { id: "inworld-Maricel", label: "Maricel", description: "Sunny & attentive · Filipino", accent: "Filipino", sampleUrl: "/voices/maricel.mp3", speechStyle: "friendly-warm" },
  { id: "inworld-Andoy", label: "Andoy", description: "Dependable & friendly · Filipino", accent: "Filipino", sampleUrl: "/voices/andoy.mp3", speechStyle: "friendly-warm" },
  { id: "minimax-May", label: "May", description: "Gentle & caring · Chinese", accent: "Chinese", sampleUrl: "/voices/may.mp3", speechStyle: "calm-reassuring" },
  { id: "minimax-Kevin", label: "Kevin", description: "Clear & natural · Chinese", accent: "Chinese", sampleUrl: "/voices/kevin.mp3", speechStyle: "professional-neutral" },
  { id: "retell-Alejandro", label: "Alejandro", description: "Confident · Spanish (Mexican)", accent: "Mexican", sampleUrl: "/voices/alejandro.mp3", speechStyle: "professional-neutral" },
  { id: "11labs-Gaby", label: "Gaby", description: "Vibrant & friendly · Spanish (Mexican)", accent: "Mexican", sampleUrl: "/voices/gaby.mp3", speechStyle: "energetic-upbeat" },
  { id: "11labs-Santiago", label: "Santiago", description: "Confident & refined · Spanish (European)", accent: "Spanish (Spain)", sampleUrl: "/voices/santiago.mp3", speechStyle: "professional-neutral" },
  { id: "minimax-Camille", label: "Camille", description: "Graceful & polished · French", accent: "French", sampleUrl: "/voices/camille.mp3", speechStyle: "professional-neutral" },
  { id: "minimax-Louis", label: "Louis", description: "Warm & relaxed · French", accent: "French", sampleUrl: "/voices/louis.mp3", speechStyle: "friendly-warm" },
  { id: "cartesia-Eva", label: "Eva", description: "Composed & capable · German", accent: "German", sampleUrl: "/voices/eva.mp3", speechStyle: "professional-neutral" },
  { id: "minimax-Max", label: "Max", description: "Playful & bright · German", accent: "German", sampleUrl: "/voices/max.mp3", speechStyle: "energetic-upbeat" },
  { id: "11labs-Noah", label: "Noah", description: "Laid-back & warm · Australian", accent: "Australian", sampleUrl: "/voices/noah.mp3", speechStyle: "friendly-warm" },
  { id: "minimax-Yumi", label: "Yumi", description: "Gentle & courteous · Japanese", accent: "Japanese", sampleUrl: "/voices/yumi.mp3", speechStyle: "calm-reassuring" },
];

/**
 * voiceId -> speaking-style key, for the blueprint prompt builder. The wizard
 * stores a real Retell voice id in config.voiceId, so prompt style must be
 * derived through this map (legacy configs may still hold a raw style key).
 */
export const VOICE_SPEECH_STYLES: Readonly<Record<string, SpeechStyle>> =
  Object.fromEntries(VOICE_OPTIONS.map((v) => [v.id, v.speechStyle]));

export const AFTER_HOURS_BEHAVIORS = [
  "take_message",
  "send_to_voicemail",
  "book_callback",
  "emergency_transfer",
] as const;
export type AfterHoursBehavior = (typeof AFTER_HOURS_BEHAVIORS)[number];

/**
 * How the receptionist covers the phone:
 *  - always: it IS the main line, answers every call 24/7
 *  - after_hours: the team answers during open hours; it covers nights/weekends
 *    (the client sets conditional forwarding to switch it on when closed)
 *  - overflow: the team's line rings first, it catches busy / no-answer / missed
 */
export const COVERAGE_MODES = ["always", "after_hours", "overflow"] as const;
export type CoverageMode = (typeof COVERAGE_MODES)[number];

// ─── Wizard config shape (persisted to agent_configs.config) ─────────────────

export interface BusinessHours {
  /** 0=Sun … 6=Sat */
  day: number;
  open: string; // "09:00"
  close: string; // "17:00"
  closed: boolean;
}

export interface FaqEntry {
  question: string;
  answer: string;
}

export interface ServiceEntry {
  name: string;
  price?: string;
  notes?: string;
}

export interface AgentOnboardingConfig {
  // Step 1 — Business basics
  businessName: string;
  category: string;
  city: string;
  province: string;
  /**
   * Business website URL (optional). At provisioning time the site is read
   * automatically and additional services/FAQs are extracted to enrich the
   * agent (never overriding what the client typed).
   */
  website: string;

  // Step 2 — Coverage + hours + after-hours
  /** How it covers the phone: main line, after-hours only, or overflow. */
  coverageMode: CoverageMode;
  /** IANA timezone the hours + bookings are interpreted in (e.g. "America/Toronto"). */
  timezone?: string;
  hours: BusinessHours[];
  afterHoursBehavior: AfterHoursBehavior;

  // Step 3 — What the agent handles
  handles: string[]; // e.g. ["booking appointments", "answering FAQs"]

  // Step 4 — Greeting / persona / voice
  greeting: string;
  persona: string;
  voiceId: string;
  /**
   * Announce up front that the caller is speaking with an AI assistant (and
   * that the call may be recorded). On by default; some regions require it.
   */
  discloseAi?: boolean;

  // Step 5 — Services & pricing
  services: ServiceEntry[];

  // Step 6 — Knowledge: structured FAQs + free-form notes + uploaded docs.
  faqs: FaqEntry[];
  /** Free-form "anything else your agent should know" (policies, promos…). */
  additionalInfo: string;
  /**
   * Storage paths (agent-knowledge bucket) of client-uploaded documents;
   * compiled into a Retell knowledge base at provisioning time.
   */
  knowledgeFilePaths: string[];

  // Step 7 — Transfer / escalation
  transferEnabled: boolean;
  transferNumber: string;
  escalationNotes: string;
  /** What it does if a transfer isn't answered (falls back gracefully). */
  transferFallback?: "take_message" | "send_to_voicemail";

  // Step 8 — Booking (Cal.com). The CLIENT's own Cal.com account: bookings
  // must land on their calendar, never on Ethos's. The wizard walks them
  // through creating a free account + API key; provisioning then auto-creates
  // the schedule and event type on THEIR account with that key.
  bookingEnabled: boolean;
  /** The client's own Cal.com API key (cal_live_...). */
  calcomApiKey: string;
  /** Optional: an existing event type id on the client's account to book into. */
  calcomEventTypeId: string;

  // Step 9 — Messages / voicemail
  voicemailGreeting: string;
  messageEmail: string;

  // Step 10 — Languages (capped + caveat)
  languages: string[]; // language codes; first is primary

  // Step 11 — Phone
  phoneChoice: "port_existing" | "new_number";
  existingNumber: string;
  /** ISO 3166-1 alpha-2 country for a new number (Twilio search). "CA", "US", … */
  newNumberCountry?: string;
  newNumberAreaCode: string; // "416" | "647" | ...
}

export type WizardStepKey =
  | "basics"
  | "hours"
  | "handles"
  | "persona"
  | "services"
  | "knowledge"
  | "transfer"
  | "booking"
  | "messages"
  | "languages"
  | "phone";

export interface WizardStepMeta {
  key: WizardStepKey;
  title: string;
  description: string;
}

export const WIZARD_STEPS: WizardStepMeta[] = [
  { key: "basics", title: "Business basics", description: "Tell us who's answering the phone." },
  { key: "hours", title: "Hours & after-hours", description: "When you're open, and what happens when you're not." },
  { key: "handles", title: "What the agent handles", description: "Pick the calls your receptionist should take." },
  { key: "persona", title: "Greeting, persona & voice", description: "How your agent sounds and introduces itself." },
  { key: "services", title: "Services & pricing", description: "What you offer, so the agent can answer accurately." },
  { key: "knowledge", title: "Knowledge & FAQs", description: "Common questions and answers." },
  { key: "transfer", title: "Transfer & escalation", description: "When to hand a call to a human." },
  { key: "booking", title: "Booking", description: "Connect Cal.com so the agent can book appointments." },
  { key: "messages", title: "Messages & voicemail", description: "How messages reach you." },
  { key: "languages", title: "Languages", description: "Which languages your agent should speak." },
  { key: "phone", title: "Phone number", description: "Port your existing number or get a new one." },
];

/** A blank config with sensible defaults, seeded from known org/plan context. */
export function emptyConfig(seed?: Partial<AgentOnboardingConfig>): AgentOnboardingConfig {
  return {
    businessName: "",
    category: "",
    city: "",
    province: "ON",
    website: "",
    hours: [0, 1, 2, 3, 4, 5, 6].map((day) => ({
      day,
      open: "09:00",
      close: "17:00",
      closed: day === 0 || day === 6,
    })),
    coverageMode: "always",
    timezone: "",
    afterHoursBehavior: "take_message",
    handles: ["answering calls", "booking appointments", "answering FAQs", "taking messages"],
    greeting: "",
    persona: "",
    voiceId: VOICE_OPTIONS[0].id,
    discloseAi: true,
    services: [],
    faqs: [],
    additionalInfo: "",
    knowledgeFilePaths: [],
    transferEnabled: false,
    transferNumber: "",
    escalationNotes: "",
    transferFallback: "take_message",
    bookingEnabled: false,
    calcomApiKey: "",
    calcomEventTypeId: "",
    voicemailGreeting: "",
    messageEmail: "",
    languages: ["en"],
    phoneChoice: "new_number",
    existingNumber: "",
    newNumberCountry: "CA",
    newNumberAreaCode: "416",
    ...seed,
  };
}

/**
 * Map a saved config to Retell string dynamic variables. Mirrors the demo
 * template's variable names so one Retell agent template serves both.
 */
export function buildDynamicVariablesFromConfig(
  config: AgentOnboardingConfig
): Record<string, string> {
  const services = config.services.map((s) => s.name).filter(Boolean);
  const faqSummary =
    config.faqs.length > 0
      ? config.faqs.map((f) => `Q: ${f.question} A: ${f.answer}`).join(" | ")
      : `${config.businessName} is a ${config.category || "local business"}${
          config.city ? ` in ${config.city}` : ""
        }.`;
  return {
    business_name: config.businessName,
    city: config.city,
    province: config.province,
    category: config.category || "local business",
    services: services.join(", "),
    faq_summary: faqSummary,
    greeting: config.greeting || `Thanks for calling ${config.businessName}! How can I help you today?`,
    books_appointments: config.bookingEnabled ? "true" : "false",
    after_hours: config.afterHoursBehavior,
    coverage_mode: config.coverageMode ?? "always",
    transfer_fallback: config.transferFallback ?? "take_message",
    disclose_ai: config.discloseAi === false ? "false" : "true",
    timezone: config.timezone || "",
    persona: config.persona,
  };
}
